import {compact} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {AnalysisTemplateSelectorTemplate} from 'analysis-tool/graphql/AnalysisTemplateSelectorQuery';
import AnalysisTemplateSelector from 'analysis-tool/templates/AnalysisTemplateSelector';
import {AnalysisTemplateGraphQL} from 'analysis-tool/templates/AnalysisTemplateTypes';
import {AnalysisTemplateFragment_AnalysisTemplateNode_dataFrameVariables_AnalysisTemplateDataFrameVariableNode as DataFrameVariableGraphQL} from 'analysis-tool/templates/graphql/__generated/AnalysisTemplateFragment';
import useAnalysisTemplateSelectorState from 'analysis-tool/templates/useAnalysisTemplateSelectorState';
import FieldActionButtons from 'metadata/FieldActionButtons';
import {HydratedTemplateConfigData} from 'procedures/design/analysis-template-config/AnalysisTemplateConfigTypes';
import {
  AnalysisSourcesForConfigOrExecution,
  AnalysisSourcesOrigin,
} from 'procedures/design/ProcedureMethodAnalysisTypes';
import {
  DataSourcesWithIcons,
  TextWithIconListItem,
} from 'procedures/shared-components/analysis/DatasetRendering';
import {
  getDatasetNameFromId,
  isDataPresentForSourceDatasetId,
} from 'procedures/shared-components/analysis/preconfiguredAnalysisUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';

function AnalysisTemplateChooser(props: {
  onSelect: (selectedTemplate: AnalysisTemplateGraphQL) => void;
  filterTemplate?: (template: AnalysisTemplateSelectorTemplate) => boolean;
}): JSX.Element {
  const [templateApplyState, , , setSelectedTemplateId] = useAnalysisTemplateSelectorState('procedure_id');

  return (
    <div style={{display: 'inline-block'}}>
      <AnalysisTemplateSelector
        templateApplyState={templateApplyState}
        onSelect={(selectedTemplate: AnalysisTemplateGraphQL) => {
          props.onSelect(selectedTemplate);
          setSelectedTemplateId(null);
        }}
        onSelectId={setSelectedTemplateId}
        selectDropdownProps={{
          dropdownToggleOverride: (
            <div>
              <Button
                buttonStyle={ButtonStyle.TEXT}
                icon="plus"
                iconAltText="Add"
                size={ButtonSize.MINI}
                text="Add analysis template"
              />
            </div>
          ),
        }}
        filterTemplate={props.filterTemplate}
      />
    </div>
  );
}

function getSourceNames(
  templateConfig: HydratedTemplateConfigData,
  sources: AnalysisSourcesForConfigOrExecution,
  datasetIdsWithMissingSource: Array<string>
): Array<string> {
  return [
    ...new Set(
      compact(
        templateConfig.config.tableMappings
          .filter(({datasetId}) => !datasetIdsWithMissingSource.includes(datasetId))
          .map(({datasetId}) => getDatasetNameFromId(datasetId, sources))
      )
    ),
  ];
}

function getInputTablesWithMissingSource(
  templateConfig: HydratedTemplateConfigData,
  datasetIdsWithMissingSource: Array<string>
): Array<DataFrameVariableGraphQL> {
  const inputTableIdsWithMissingSource = templateConfig.config.tableMappings
    .filter(({datasetId}) => datasetIdsWithMissingSource.includes(datasetId))
    .map((tableMapping) => tableMapping.analysisTemplateInputTableId);
  return templateConfig.template.dataFrameVariables.filter((dataFrameVariable) =>
    inputTableIdsWithMissingSource.includes(dataFrameVariable.id)
  );
}

/**
 * Renders a table of analysis templates with a row for each template and an additional row for adding a new template.
 * This table only lists templates which have already been configured for later analysis against the current procedure.
 *
 * When the disabled prop is true, the additional row for adding a new template is not displayed. This is useful when the table should be read-only.
 *
 * @param templateConfigs - The list of template configurations to display in the table.
 * @param sources - The list of available data sources for this method
 * @param onSelectTemplate - The callback to call when a template is selected.
 * @param filterTemplate - An optional filter function to apply to the templates.
 * @param disabled - An optional flag to hide the add new template row.
 * @returns The rendered table.
 */
export default function AnalysisTemplateTable({
  templateConfigs,
  sources,
  onSelectTemplate,
  onRemoveTemplate,
  filterTemplate,
  disabled,
}: {
  templateConfigs: Array<HydratedTemplateConfigData>;
  sources: AnalysisSourcesForConfigOrExecution | null;
  onSelectTemplate: (selectedTemplate: AnalysisTemplateGraphQL) => void;
  onRemoveTemplate: (templateId: string) => void;
  filterTemplate?: (template: AnalysisTemplateSelectorTemplate) => boolean;
  disabled?: boolean;
}): JSX.Element {
  const numConfiguredTemplates = templateConfigs.length;

  /** The last row in the table is for the add template button, so it has different rendering logic */
  function isRowWithTemplate(rowIndex: number): boolean {
    return rowIndex < numConfiguredTemplates;
  }

  function doesRowHaveMissingSourceError(rowIndex: number): boolean {
    // Do not show error if row is not a template row or if sources are not yet loaded
    if (!isRowWithTemplate(rowIndex) || sources == null) {
      return false;
    }
    return templateConfigs[rowIndex].config.tableMappings.some(
      ({datasetId}) =>
        !isDataPresentForSourceDatasetId(datasetId, sources, AnalysisSourcesOrigin.CONFIGURATION)
    );
  }

  function handleRemoveAnalysisTemplate(rowIndex: number): void {
    onRemoveTemplate(templateConfigs[rowIndex].template.id);
  }

  function handleEditAnalysisTemplate(rowIndex: number): void {
    onSelectTemplate(templateConfigs[rowIndex].template);
  }

  const renderAnalysisTemplateCell = (rowIndex: number): JSX.Element => (
    <Cell className={css(styles.templateNameColumn)}>
      {isRowWithTemplate(rowIndex) ? (
        <div
          className={css(styles.cellContents, styles.templateCellContents)}
          {...getTestProps({
            component: 'AnalysisTemplateTable',
            element: 'templateCell',
            key: templateConfigs[rowIndex].template.id,
          })}
        >
          <TextWithIconListItem text={templateConfigs[rowIndex].template.name} iconName="template" />
          <a href={templateConfigs[rowIndex].template.editUrl} target="blank" rel="noopener noreferrer">
            <BenchlingIcon icon="arrow-up-right-from-square" />
          </a>
        </div>
      ) : (
        <AnalysisTemplateChooser onSelect={onSelectTemplate} filterTemplate={filterTemplate} />
      )}
    </Cell>
  );

  const renderSourceDataCell = (rowIndex: number): JSX.Element => {
    if (!isRowWithTemplate(rowIndex) || sources == null) {
      return <Cell className={css(styles.dataColumn)} />;
    }

    const maxTotalCharacters = 110;

    const datasetIdsWithMissingSource = templateConfigs[rowIndex].config.tableMappings
      .map(({datasetId}) => datasetId)
      .filter(
        (datasetId) =>
          !isDataPresentForSourceDatasetId(datasetId, sources, AnalysisSourcesOrigin.CONFIGURATION)
      );
    const inputTablesWithMissingSource = getInputTablesWithMissingSource(
      templateConfigs[rowIndex],
      datasetIdsWithMissingSource
    );
    const missingSourceNames = inputTablesWithMissingSource.map((df) => `${df.name} (missing)`);

    return (
      <Cell className={css(styles.dataColumn)}>
        <div className={css(styles.cellContents)}>
          <div className={css(styles.missingSource)}>
            <DataSourcesWithIcons
              iconName="circle-xmark"
              dataSources={missingSourceNames.map((name) => ({name}))}
              maxItems={2} // These names tend to be longer, so limit max items to 2
              maxCharacterCount={maxTotalCharacters}
              onlyEnablePopoverOnOverflow={true}
            />
          </div>
          <div
            {...getTestProps({
              component: 'AnalysisTemplateTable',
              element: 'presentDataSources',
              key: templateConfigs[rowIndex].template.id,
            })}
          >
            <DataSourcesWithIcons
              iconName="dataset"
              dataSources={getSourceNames(
                templateConfigs[rowIndex],
                sources,
                datasetIdsWithMissingSource
              ).map((sourceName) => ({
                name: sourceName,
              }))}
              maxItems={5}
              maxCharacterCount={maxTotalCharacters}
              onlyEnablePopoverOnOverflow={true}
            />
          </div>
        </div>
      </Cell>
    );
  };

  const renderActionsCell = (rowIndex: number): JSX.Element => (
    <Cell>
      {isRowWithTemplate(rowIndex) && !disabled ? (
        <FieldActionButtons
          // Edit mode gives us cancel and complete buttons, which we don't want since editing is done via a modal
          isEditing={false}
          edit={() => {
            handleEditAnalysisTemplate(rowIndex);
          }}
          remove={() => {
            handleRemoveAnalysisTemplate(rowIndex);
          }}
        />
      ) : null}
    </Cell>
  );

  return (
    <Table
      numRows={disabled ? numConfiguredTemplates : numConfiguredTemplates + 1}
      isCompact={true}
      getRowClassName={(rowIndex) => css(doesRowHaveMissingSourceError(rowIndex) && styles.rowWithError)}
    >
      <Column header="Analysis template" renderCell={renderAnalysisTemplateCell} />
      <Column header="Source data" renderCell={renderSourceDataCell} />
      <Column renderCell={renderActionsCell} />
    </Table>
  );
}

const styles = StyleSheet.create({
  templateNameColumn: {width: '30%'},
  // Color matches header color provided by the table
  dataColumn: {color: Color.GRAY_700, width: '70%'},
  cellContents: {paddingTop: '4px', paddingBottom: '4px'},
  templateCellContents: {display: 'flex'},
  rowWithError: {backgroundColor: Color.RED_100},
  missingSource: {color: Color.RED_500, paddingBottom: '4px'},
});
